import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { getCTBottomRightSubInfo, getSelectedSeries, getSelectedSeriesId, getSelectedSeriesImageTag, getSelectedStudy,  getSelectedStudyId,  getSelectedStudySeries,  getSelectedStudySeriesImageTags,  getStudyViewState, getTitleInfo, getTopLeftInfo, getTopRightInfo, getDefaultPanelStates, getSeriesSections, getSeriesWindowWidth, getSeriesWindowLevel } from './BrowserPageSelectors';
import { SORT_TYPE, StudyFilter, StudySortKeys } from 'config/studyConfig';
import { clearStudy, clearStudyViewState, deleteStudy, setFilters, setPagination, setSelectedStudy, setSeries, setSeriesSelections, setSortOptions, setStudies } from './BrowserPageSlice';
import { Study} from '@models/Study';
import { RootState } from '@root/rootReducer';
import { Series } from '@models/Series';
import { ImageTag } from '@models/ImageTag';
import { PanelStates } from 'config/analysisConfig';
import { SeriesSelection } from 'config/browserConfig';

type PaginationPayload = {
  currentPage?: number;
  perPage?: number;
};

export interface BrowserPageHookResult {
  StudyViewState: ReturnType<typeof getStudyViewState>;
  SelectedStudy: ReturnType<typeof getSelectedStudy>;

  recentStudies : Study[];

  selectedStudy: Study|undefined;
  selectedStudyId: string | null;
  selectedStudySeries: Series[];
  selectedStudySeriesImageTags: ImageTag[];

  selectedSeries: Series|undefined;
  selectedSeriesId: string|undefined;
  selectedSeriesImageTag : ImageTag | undefined;
  selectedSeriesImageTagWindowLevel : number;
  selectedSeriesImageTagWindowWidth : number;
  defaultPanelStates : Partial<PanelStates>;

  seriesSelections : SeriesSelection[];

  titleInfo:string;
  topLeftInfo:string[];
  topRightInfo:string[];
  CTBottomRightSubInfo:string;

  updateStudies: (value: Study[]) => void;
  updateSeries: (value: Series[]) => void;
  // updateRecentStudy: (value : Study) => void;
  updateFilters: (value: StudyFilter) => void;
  updateSelectedStudy: (value : Study) => void;
  deleteSelectedStudy: (value : Study) => void;
  updateSortOptions: (value: {
    sortBy?: StudySortKeys;
    sortDirection?: typeof SORT_TYPE[keyof typeof SORT_TYPE];
  }) => void;
  updateSeriesSections: (value: SeriesSelection[]) => void;
  updatePagination: (value: PaginationPayload) => void;
  resetStudyViewState: () => void;
}

export const useBrowserPage = (): BrowserPageHookResult => {
  const dispatch = useDispatch();

  // 값 읽기
  const StudyViewState = useSelector(getStudyViewState, shallowEqual);

  const SelectedStudy = useSelector(getSelectedStudy, shallowEqual);


  const recentStudies = useSelector((state: RootState) => state.study.recentStudies,shallowEqual);

  const selectedStudy = useSelector(getSelectedStudy, shallowEqual);
  const selectedStudyId = useSelector(getSelectedStudyId, shallowEqual);
  const selectedStudySeries = useSelector(getSelectedStudySeries,shallowEqual);
  const selectedStudySeriesImageTags = useSelector(getSelectedStudySeriesImageTags, shallowEqual);

  const selectedSeries = useSelector(getSelectedSeries,shallowEqual);
  const selectedSeriesId = useSelector(getSelectedSeriesId, shallowEqual);
  const selectedSeriesImageTag = useSelector(getSelectedSeriesImageTag, shallowEqual);
  const selectedSeriesImageTagWindowLevel = useSelector(getSeriesWindowLevel,shallowEqual);
  const selectedSeriesImageTagWindowWidth = useSelector(getSeriesWindowWidth,shallowEqual);
  const defaultPanelStates = useSelector(getDefaultPanelStates,shallowEqual);

  const titleInfo = useSelector(getTitleInfo, shallowEqual);
  const topLeftInfo = useSelector(getTopLeftInfo,shallowEqual);
  const topRightInfo = useSelector(getTopRightInfo, shallowEqual);
  const CTBottomRightSubInfo = useSelector(getCTBottomRightSubInfo,shallowEqual);

  const seriesSelections = useSelector(getSeriesSections, shallowEqual);

  // 값 변경
  const updateStudies = useCallback((value: Study[]) => {
    dispatch(setStudies(value))
  }, [dispatch])

  const updateSeries = useCallback((value: Series[]) => {
    dispatch(setSeries(value))
  }, [dispatch])

  // const updateRecentStudy = useCallback((payload: Study) => {
  //   dispatch(addRecentStudy(payload));
  // }, [dispatch]);

  const updateSelectedStudy = useCallback((study : Study) => {
    dispatch(setSelectedStudy(study))
  }, [dispatch])

  const deleteSelectedStudy = useCallback((study : Study) => {
    dispatch(deleteStudy(study))
  }, [dispatch])

  const updateFilters = useCallback((value: StudyFilter) => {
    dispatch(setFilters(value))
  }, [dispatch])

  const updateSortOptions = useCallback(
    (value: {
      sortBy?: StudySortKeys;
      sortDirection?: typeof SORT_TYPE[keyof typeof SORT_TYPE];
    }) => {
      dispatch(setSortOptions(value));
    },
    [dispatch]
  );

  const updatePagination = useCallback((payload: PaginationPayload) => {
    if (payload.currentPage === undefined && payload.perPage === undefined) return;
    dispatch(setPagination(payload));
  }, [dispatch])

  const updateSeriesSections = useCallback((payload: SeriesSelection[]) => {
    dispatch(setSeriesSelections(payload));
  }, [dispatch])

  const resetStudyViewState = useCallback(() => {
    dispatch(clearStudyViewState());
    dispatch(clearStudy());
  }, [dispatch]);

  return useMemo(() => ({
    StudyViewState,
    SelectedStudy,

    recentStudies,
    selectedStudy,
    selectedStudyId,
    selectedStudySeries,
    selectedStudySeriesImageTags,
    selectedSeriesImageTagWindowLevel,
    selectedSeriesImageTagWindowWidth,
    selectedSeries,
    selectedSeriesId,
    selectedSeriesImageTag,

    defaultPanelStates,
    seriesSelections,

    titleInfo,
    topLeftInfo,
    topRightInfo,
    CTBottomRightSubInfo,

    updateStudies,
    updateSeries,
    // updateRecentStudy,
    updateFilters,
    updateSelectedStudy,
    deleteSelectedStudy,
    updateSortOptions,
    updatePagination,
    updateSeriesSections,
    resetStudyViewState,
  }), [
        StudyViewState,
        SelectedStudy,

        recentStudies,
        selectedStudy,
        selectedStudyId,
        selectedStudySeries,
        selectedStudySeriesImageTags,

        selectedSeries,
        selectedSeriesId,
        selectedSeriesImageTag,

        defaultPanelStates,
        seriesSelections,

        titleInfo,
        topLeftInfo,
        topRightInfo,
        CTBottomRightSubInfo,

        updateStudies,
        updateSeries,
        // updateRecentStudy,
        updateFilters,
        updateSelectedStudy,
        deleteSelectedStudy,
        updateSortOptions,
        updatePagination,
        updateSeriesSections,
        resetStudyViewState
      ]);
}
